import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCog } from '@fortawesome/free-solid-svg-icons';
import { faPatreon, faTwitch } from '@fortawesome/free-brands-svg-icons';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';

import Section from '../components/Section';
import Loading from '../components/Loading';

import utils from '../util/utils';
import api from '../util/api';
import login from '../util/login';

const linkregex = /[?#&\s]/g;
const twitchTiers = {
    '1000': { thanks: true, refresh: true },
    '2000': { thanks: true, refresh: true },
    '3000': { thanks: true, refresh: true }
};
const patreonTiers = {
    '3412502': { thanks: true }, // Thank You
    '3530379': { thanks: true, refresh: true }, // Character Refreshes (Retired)
    '5543724': { thanks: true, refresh: true }, // Streamer Link (Retired)
    '4585863': { thanks: true, refresh: true, guild: true }, // Guild Refreshes (Retired)

    '6263109': { thanks: true, refresh: true }, // Character Refreshes
    '6263104': { thanks: true, refresh: true, guild: true }, // Guild Refreshes

    '6263128': { thanks: true, refresh: true, guild: true }, // Farmer
    '6263130': { thanks: true, refresh: true, guild: true }, // The Fabulous
    '6263139': { thanks: true, refresh: true, guild: true } // The Insane

    //'11864919': 0 // Free
};

export default class Rewards extends Component {
    constructor(props) {
        super(props);
        this.state = {
            ready: false,
            blizzardShowLogin: false,

            patreonConnected: false,
            patreonTier: null,
            twitchConnected: false,
            twitchTier: null,

            inputTwitch: '',
            inputYouTube: '',
            inputTwitter: '',
            inputLinktree: '',
            thanks: false,
            refresh: false,
            guild: '',

            saving: false,
            saved: false,
            guilds: null
        };
    }

    componentDidMount() {
        this.init();
    }

    init = async () => {
        try {
            const token = this.blizzardToken();
            if (!token) return; // not logged in

            if (!login.jwt.get()) {
                // TODO shouldn't happen but can we make an API call to get a JWT token
                this.setState({ blizzardShowLogin: true });
                return;
            }

            // finalize any connect requests
            await this.loadPatreon(token);
            await this.loadTwitch(token);

            // load existing connections and rewards
            await this.loadRewards();

            // async load list of guilds
            this.loadGuilds(token);
        } finally {
            this.setState({ ready: true });
        }
    }

    blizzardToken = () => {
        const storage = localStorage.getItem('token.blizzard');
        const token = storage ? JSON.parse(storage) : null;

        // validate user token
        if (!token?.region || !token?.expires_at) {
            this.setState({ blizzardShowLogin: true });
            return null;
        } else if (token.expires_at < Date.now()) {
            // expired token
            this.props.history.push('/redirect?to=blizzard&from=' + encodeURIComponent('/rewards') + '&region=' + encodeURIComponent(token.region));
            return null;
        }

        this.setState({ blizzardShowLogin: false });
        return token;
    }

    loadGuilds = async (token) => {
        // Blizzard API call
        const response = await api.post('user/' + encodeURIComponent(token.region), token);
        if (response.status !== 200) return null;

        const json = await response.json();
        if (!json?.characters?.length) return null;

        // load stubs from the database
        const response2 = await api.post('stubs', json.characters);
        if (response2.status !== 200) return null;

        const json2 = await response2.json();

        // gather a list of unique guilds
        const parseGuilds = () => {
            const guilds = {};
            for (let c of json2) {
                if (!c.character?.guildName) continue;

                const key = utils.clean(c.character.region) + '#' + utils.slug(c.character.guildRealm) + '#' + utils.slug(c.character.guildName);
                const name = c.character.guildName + '-' + c.character.guildRealm;
                guilds[key] = { key, name };
            }
            return Object.values(guilds);
        }

        // sort the list of guilds
        const guilds = parseGuilds();
        guilds.sort((a, b) => utils.collator.compare(a.name, b.name));

        this.setState({ guilds });
    }

    loadPatreon = async (blizzard) => {
        const storage = localStorage.getItem('token.patreon');
        localStorage.removeItem('token.patreon');

        const token = storage ? JSON.parse(storage) : null;
        if (token?.expires_at && (token.expires_at > Date.now())) {
            // user is coming to the page trying to connect their patreon
            const response = await api.post('connect', {
                blizzard: {
                    region: blizzard.region,
                    token: blizzard.access_token
                },
                patreon: {
                    token: token.access_token
                }
            });

            if (response.status !== 204) {
                alert('An unknown error occurred. Please try again in a few minutes.');
                return;
            }
        }
    }

    disconnectPatreon = async () => {
        return this.disconnect('patreon');
    }

    loadTwitch = async (blizzard) => {
        const storage = localStorage.getItem('token.twitch');
        localStorage.removeItem('token.twitch');

        const token = storage ? JSON.parse(storage) : null;
        if (token?.expires_at && (token.expires_at > Date.now())) {
            // user is coming to the page trying to connect their twitch
            const response = await api.post('connect', {
                blizzard: {
                    region: blizzard.region,
                    token: blizzard.access_token
                },
                twitch: {
                    token: token.access_token
                }
            });

            if (response.status !== 204) {
                alert('An unknown error occurred. Please try again in a few minutes.');
                return;
            }
        }
    }

    disconnectTwitch = async () => {
        return this.disconnect('twitch');
    }

    disconnect = async (type) => {
        const blizzard = this.blizzardToken();
        const payload = {
            blizzard: {
                region: blizzard.region,
                token: blizzard.access_token
            }
        };
        payload[type] = { disconnect: true };

        const response = await api.post('connect', payload);
        if (response.status !== 204) {
            alert('An unknown error occurred. Please try again in a few minutes.');
            return;
        }

        this.loadRewards();
    }

    loadRewards = async () => {
        const response = await api.get('rewards?jwt=' + encodeURIComponent(login.jwt.get()));
        if (response.status !== 200) {
            alert('An unknown error occurred. Please try again in a few minutes.');
            return;
        }

        const json = await response.json();
        this.setState({
            patreonConnected: json.patreon_id, patreonTier: json.patreon_tier,
            twitchConnected: json.twitch_id, twitchTier: json.twitch_tier,
            thanks: json.thanks, refresh: json.refresh, guild: json.guild || '',
            inputTwitch: json.links?.twitch || '', inputYouTube: json.links?.youtube || '', inputTwitter: json.links?.twitter || '', inputLinktree: json.links?.linktree || ''
        });
    }

    saveRewards = async () => {
        this.setState({ saving: true, saved: false });
        const payload = {
            thanks: this.state.thanks,
            refresh: this.state.refresh,
            guild: this.state.guild,

            links: {
                twitch: this.state.inputTwitch,
                youtube: this.state.inputYouTube,
                twitter: this.state.inputTwitter,
                linktree: this.state.inputLinktree
            }
        };

        const response = await api.post('rewards?jwt=' + encodeURIComponent(login.jwt.get()), payload);
        if (response.status !== 200) {
            alert('An unknown error occurred. Please try again in a few minutes.');
            this.setState({ saving: false, saved: false });
            return;
        }

        this.setState({ saving: false, saved: true });
    }

    handleThanks = async (event, thanks) => {
        this.setState({ thanks, saved: false });
    }

    handleRefresh = async (event, refresh) => {
        this.setState({ refresh, saved: false });
    }

    handleTwitch = (event) => {
        this.setState({ inputTwitch: event.target.value.replace(linkregex, ''), saved: false });
    }

    handleYouTube = (event) => {
        this.setState({ inputYouTube: event.target.value.replace(linkregex, ''), saved: false });
    }

    handleTwitter = (event) => {
        this.setState({ inputTwitter: event.target.value.replace(linkregex, ''), saved: false });
    }

    handleLinktree = (event) => {
        this.setState({ inputLinktree: event.target.value.replace(linkregex, ''), saved: false });
    }

    handleGuild = (event) => {
        this.setState({ guild: event.target.value || '', saved: false });
    }

    render() {
        if (!this.state.ready) return <Loading message="Loading..." />;

        return (
            <>
                {this.renderIntro()}
                {this.renderConnect()}
                {this.renderRewards()}
            </>
        );
    }

    renderIntro() {
        if (this.state.blizzardShowLogin) {
            return (
                <Section title="Supporter Rewards">
                    Please login using Battle.net in order to claim your rewards
                </Section>
            );
        }

        return (
            <Section title="Supporter Rewards">
                <p>Thank you for choosing to support <i>Data for Azeroth</i>! Your contributions help cover hosting costs and keep the site running.</p>

                Please claim your rewards below. Any re-subscriptions or cancellations will be automatically processed every hour. Feel free to contact me on <a href="https://discord.gg/7Mmjxdh" target="external">Discord</a> if you need any help. Thank you!
            </Section>
        );
    }

    renderConnect() {
        if (this.state.blizzardShowLogin) return null; // don't show

        const providers = [
            {
                key: 'patreon', name: 'Patreon', icon: faPatreon, connected: this.state.patreonConnected, tier: patreonTiers[this.state.patreonTier], disconnect: this.disconnectPatreon,
                button: <a href="https://www.patreon.com/shoogen" target="external" className="btn btn-patreon text-nowrap mr-2"><FontAwesomeIcon icon={faPatreon} className="mr-2" />Become a Patron</a>
            },
            {
                key: 'twitch', name: 'Twitch', icon: faTwitch, connected: this.state.twitchConnected, tier: twitchTiers[this.state.twitchTier], disconnect: this.disconnectTwitch,
                button: <a href="https://twitch.tv/shoogen" target="external" className="btn btn-twitch text-nowrap mr-2"><FontAwesomeIcon icon={faTwitch} className="mr-2" />Subscribe (Free w/ Prime)</a>
            }
        ];

        return (
            <Section title="Connections">
                {providers.map(provider => (
                    <div key={provider.key}>
                        <h5><span className={'mr-2 badge btn-' + provider.key}><FontAwesomeIcon icon={provider.icon} /></span>{provider.name}</h5>
                        {provider.connected ?
                            <>
                            <p>Account Connected</p>
                            {provider.tier ? null : <p>Your account is not eligible for any rewards. Please make sure you have an active subscription, then return to this page and click <b>Check Subscription</b>.</p>}
                            </>
                        :
                            <p>To claim your rewards, first make sure you have an active subscription, then return to this page and connect your account.</p>
                        }
                        <p>
                            {provider.connected && provider.tier ? null : provider.button}
                            {provider.connected && provider.tier ? null : <Link to={'/redirect?to=' + encodeURIComponent(provider.key) + '&from=' + encodeURIComponent('/rewards')} className={'text-nowrap mr-2 btn btn-' + provider.key}>{provider.connected ? 'Check Subscription' : 'Connect Account'}</Link>}
                            {provider.connected ? <button className={'btn btn-' + provider.key} onClick={provider.disconnect}>Disconnect</button> : null}
                        </p>
                    </div>
                ))}
            </Section>
        );
    }

    patreonTag() {
        return <a href="https://www.patreon.com/shoogen" target="external" className="btn btn-patreon badge"><FontAwesomeIcon icon={faPatreon} className="mr-2" />Patreon</a>;
    }

    twitchTag() {
        return <a href="https://twitch.tv/shoogen" target="external" className="btn btn-twitch badge"><FontAwesomeIcon icon={faTwitch} className="mr-2" />Twitch</a>;
    }

    mergeTiers() {
        //return { thanks: true, refresh: true, guild: true };
        return {
            ...twitchTiers[this.state.twitchTier],
            ...patreonTiers[this.state.patreonTier]
        };
    }

    renderRewards() {
        let tier = this.mergeTiers();
        let preview = !Object.keys(tier).length;
        const rewards = [];

        // Tier 0
        if (preview) {
            rewards.push(
                <div key="header">
                    <p>
                        If you are enjoying the site, please consider becoming a supporter and get access to the following rewards!
                    </p>
                </div>
            );
        }

        // Tier 1
        if (preview) {
            rewards.push(
                <div key="rewards1">
                    <h5>Thank You Tier {this.patreonTag()}</h5>
                    <ul>
                        <li>A <i>Thank You</i> ribbon will be shown on your character's profile page and leaderboard rankings. Your main character will also appear on the <i>Thank You</i> page.</li>
                        <li>You will not be shown any advertisements on the site.</li>
                        <li>Receive a special role in the <i>Data for Azeroth</i> Discord server with access to special channels.</li>
                        <li>Links to your Twitch and YouTube channels can be displayed on your character's profile page and leaderboard rankings.</li>
                    </ul>
                </div>
            );
        } else if (tier.thanks) {
            // Thank You
            rewards.push(
                <div key="rewards1">
                    <h5>Thank You</h5>
                    <p>A <i>Thank You</i> ribbon will be shown on your character's profile page and leaderboard rankings. Your main character will also appear on the <i>Thank You</i> page.</p>
                    <div className="text-center"><FormControlLabel control={<Switch checked={this.state.thanks} onChange={this.handleThanks} />} label={this.state.thanks ? 'Enabled' : 'Disabled'} /></div>
                </div>
            );

            // Discord
            if (this.state.patreonTier) {
                rewards.push(<div key="discord"><h5>Discord</h5><p>Please set up this reward by logging in to Patreon, and connect your Discord account within the settings.</p></div>);
            } else if (this.state.twitchTier) {
                rewards.push(<div key="discord"><h5>Discord</h5><p>Please set up this reward by logging in to Discord, and connect your Twitch account within the settings.</p></div>);
            }

            // Streamer Links
            rewards.push(
                <div key="links">
                    <h5>Streamer Links</h5>
                    <p>The following links will be shown on your character's profile page and leaderboard rankings. You may leave any link blank if you do not want it shown.</p>
                    <div className="col-lg-6 col-md-8 mx-auto">
                        <div className="input-group mb-1">
                            <div className="input-group-prepend">
                                <span className="input-group-text">https://twitch.tv/</span>
                            </div>
                            <input type="text" className="form-control" value={this.state.inputTwitch || ''} onChange={this.handleTwitch} />
                        </div>
                        <div className="input-group mb-1">
                            <div className="input-group-prepend">
                                <span className="input-group-text">https://youtube.com/</span>
                            </div>
                            <input type="text" className="form-control" value={this.state.inputYouTube || ''} onChange={this.handleYouTube} />
                        </div>
                        <div className="input-group mb-1">
                            <div className="input-group-prepend">
                                <span className="input-group-text">https://x.com/</span>
                            </div>
                            <input type="text" className="form-control" value={this.state.inputTwitter || ''} onChange={this.handleTwitter} />
                        </div>
                        <div className="input-group mb-1">
                            <div className="input-group-prepend">
                                <span className="input-group-text">https://linktr.ee/</span>
                            </div>
                            <input type="text" className="form-control" value={this.state.inputLinktree || ''} onChange={this.handleLinktree} />
                        </div>
                    </div>
                </div>
            );
        }

        // Tier 2
        if (preview) {
            rewards.push(
                <div key="rewards2">
                    <h5>Character Refresh Tier {this.patreonTag()} {this.twitchTag()}</h5>
                    <ul>
                        <li>All previous rewards, plus:</li>
                        <li>Your characters will be added to a priority queue that keeps them automatically updated.</li>
                    </ul>
                </div>
            );
        } else if (tier.refresh) {
            rewards.push(
                <div key="rewards2">
                    <h5>Character Refreshes</h5>
                    <p>Your characters will be automatically refreshed.</p>
                    <div className="text-center"><FormControlLabel control={<Switch checked={this.state.refresh} onChange={this.handleRefresh} />} label={this.state.refresh ? 'Enabled' : 'Disabled'} /></div>
                </div>
            );
        }

        // Tier 3
        if (preview) {
            rewards.push(
                <div key="rewards3">
                    <h5>Guild Refresh Tier {this.patreonTag()}</h5>
                    <ul>
                        <li>All previous rewards, plus:</li>
                        <li>Your guild will be added to a priority queue that keeps all members automatically updated.</li>
                    </ul>
                </div>
            );
        } else if (tier.guild) {
            rewards.push(
                <div key="rewards3">
                    <h5>Guild Refreshes</h5>
                    <p>Please select which guild you would like to have automatically refreshed.</p>
                    <div className="d-flex justify-content-center">
                        <div>
                            {this.state.guilds ?
                                <select id="rewardguild" className="form-control" style={{ minWidth: '10em' }} value={this.state.guild} onChange={this.handleGuild}>
                                    <option key="empty" value=""></option>
                                    {this.state.guilds.map(guild => <option key={guild.key} value={guild.key}>{guild.name}</option>)}
                                </select>
                            : <FontAwesomeIcon icon={faCog} spin />}
                        </div>
                    </div>
                </div>
            );
        }

        // Footer
        if (preview) {
            rewards.push(
                <div key="footer" className="d-flex flex-wrap justify-content-center mt-3">
                    <a href="https://www.patreon.com/shoogen" target="external" className="btn btn-patreon text-nowrap mr-2"><FontAwesomeIcon icon={faPatreon} className="mr-2" />Become a Patron</a>
                    <a href="https://twitch.tv/shoogen" target="external" className="btn btn-twitch text-nowrap"><FontAwesomeIcon icon={faTwitch} className="mr-2" />Subscribe (Free w/ Prime)</a>
                </div>
            );
        } else if (this.state.saving) {
            rewards.push(
                <div key="save" className="text-center mt-3">
                    <button className="btn btn-primary" disabled><FontAwesomeIcon icon={faCog} spin /> Saving...</button>
                </div>
            );
        } else {
            rewards.push(
                <div key="save" className="text-center mt-3">
                    <button className="btn btn-primary" onClick={this.saveRewards}>Save</button>
                    {this.state.saved ? <div>Rewards Saved!</div> : null}
                </div>
            );
        }

        return (
            <Section title={preview ? 'Not Yet a Supporter?' : 'Your Rewards'}>
                {rewards}
            </Section>
        );
    }
}